import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { MessageSquare, Check, X } from 'lucide-react'

const API = process.env.REACT_APP_BACKEND_URL

const COULEURS = [
  { id: 'jaune', hex: '#FCD34D', label: 'Jaune' },
  { id: 'marron', hex: '#A16207', label: 'Marron' },
  { id: 'rose', hex: '#F472B6', label: 'Rose' },
  { id: 'violet', hex: '#A78BFA', label: 'Violet' },
  { id: 'vert', hex: '#34D399', label: 'Vert' },
  { id: 'bleu', hex: '#60A5FA', label: 'Bleu' },
  { id: 'gris', hex: '#9CA3AF', label: 'Gris' },
]

function getHex(couleurId) {
  return COULEURS.find(c => c.id === couleurId)?.hex || '#9CA3AF'
}

export function NoteGestionnaireCell({ sinistre, onUpdated, grande = false }) {
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [note, setNote] = useState(sinistre.note_gestionnaire || '')
  const [couleur, setCouleur] = useState(sinistre.note_couleur || 'gris')
  const [saving, setSaving] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      // Check if click is outside the trigger button AND outside the portal popover
      const popover = document.querySelector('[data-testid="note-gestionnaire-popover"]')
      if (ref.current && !ref.current.contains(e.target) && (!popover || !popover.contains(e.target))) {
        setOpen(false)
        setEditing(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  async function handleSave() {
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note_gestionnaire: note, note_couleur: couleur }),
      })
      if (res.ok) {
        setEditing(false)
        setOpen(false)
        if (onUpdated) onUpdated()
      }
    } finally {
      setSaving(false)
    }
  }

  const hex = getHex(sinistre.note_couleur || couleur)
  const hasNote = sinistre.note_gestionnaire

  return (
    <div className="relative" ref={ref} onClick={e => e.stopPropagation()}>
      {/* Compact cell display */}
      <button
        onClick={() => { setOpen(!open); setNote(sinistre.note_gestionnaire || ''); setCouleur(sinistre.note_couleur || 'gris') }}
        className="flex items-center gap-1.5 max-w-[280px] group"
        data-testid="note-gestionnaire-cell"
      >
        {hasNote ? (
          <>
            <span
              className={grande ? 'w-4 h-4 rounded-full shrink-0 ring-2 ring-white shadow' : 'w-2.5 h-2.5 rounded-full shrink-0'}
              style={{ backgroundColor: hex }}
              data-testid="note-pastille"
            />
            <span className="text-xs text-slate-700 truncate">
              {sinistre.note_gestionnaire.length > 60
                ? sinistre.note_gestionnaire.slice(0, 60) + '...'
                : sinistre.note_gestionnaire}
            </span>
          </>
        ) : (
          <span className="text-slate-300 group-hover:text-slate-500 transition-colors">
            <MessageSquare size={14} />
          </span>
        )}
      </button>

      {/* Popover via Portal — sort du DOM du tableau pour eviter le clipping */}
      {open && createPortal(
        <div className="fixed z-[9999] w-80 bg-white rounded-lg shadow-xl border border-slate-200 p-3"
             style={(() => {
               if (!ref.current) return { top: 0, left: 0 }
               const rect = ref.current.getBoundingClientRect()
               const left = Math.min(rect.left, window.innerWidth - 340)
               const POPOVER_H = 260
               if (rect.bottom + POPOVER_H > window.innerHeight) {
                 return { bottom: window.innerHeight - rect.top + 4, left }
               }
               return { top: rect.bottom + 4, left }
             })()}
             data-testid="note-gestionnaire-popover"
             onClick={e => e.stopPropagation()}>
          {!editing ? (
            <>
              {hasNote ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: hex }} />
                    <span className="text-xs font-medium text-slate-500">Note gestionnaire</span>
                  </div>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{sinistre.note_gestionnaire}</p>
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">Aucune note</p>
              )}
              <button
                onClick={() => setEditing(true)}
                className="mt-3 w-full text-xs text-center py-1.5 bg-slate-100 hover:bg-slate-200 rounded text-slate-600 transition-colors"
                data-testid="note-edit-btn"
              >
                {hasNote ? 'Modifier' : 'Ajouter une note'}
              </button>
            </>
          ) : (
            <div className="space-y-3">
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                className="w-full h-20 text-sm border border-slate-200 rounded-md p-2 resize-none outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Note..."
                autoFocus
                data-testid="note-textarea"
              />
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] text-slate-400 mr-1">Couleur :</span>
                {COULEURS.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setCouleur(c.id)}
                    className={`w-6 h-6 rounded-full border-2 transition-transform ${
                      couleur === c.id ? 'border-slate-800 scale-110' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: c.hex }}
                    title={c.label}
                    data-testid={`note-color-${c.id}`}
                  />
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setEditing(false); setOpen(false) }}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                  data-testid="note-cancel-btn"
                >
                  <X size={14} />
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  data-testid="note-save-btn"
                >
                  <Check size={12} />
                  {saving ? '...' : 'OK'}
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}

export { COULEURS, getHex }
